// Layer 4 (UI) — "Year 1 feed against budget" (locked comp `planning.html` A):
// cumulative feed less cumulative budget, so the budget is the zero line. The
// actual runs to the to-date point; the three scenarios fan out from it to
// Feb 2027; stretch is a dotted line. The area under zero is the one abnormal
// reading, so it takes the state-warning wash. Hand-drawn SVG, as Downtime's
// availability chart: `TrendChart` has no fan or signed baseline.
// Renders YearAgainstBudget.transform.ts; prints no figure of its own.

import { useLayoutEffect, useRef, useState } from 'react';
import type { PlanningScenarioName } from './Scene.transform';
import { formatVsBudgetTick, type AgainstBudgetPoint, type YearAgainstBudgetView } from './YearAgainstBudget.transform';

/** Drawn at the card's measured width so type stays at its set size at
 *  every breakpoint instead of scaling with a fixed viewBox. */
const FALLBACK_W = 770;

function useMeasuredWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(FALLBACK_W);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const measure = () => setWidth(Math.max(320, Math.round(node.clientWidth)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

function lastOf<T>(items: readonly T[]): T {
  return items[items.length - 1]!;
}
const PAD = { l: 52, r: 96, t: 12, b: 30 };

export function YearAgainstBudgetChart({
  view,
  selected,
  height = 232,
}: {
  view: YearAgainstBudgetView;
  selected: PlanningScenarioName;
  height?: number;
}) {
  const [ref, W] = useMeasuredWidth();
  const [lo, hi] = view.domain;
  const x = (m: number) => PAD.l + ((W - PAD.l - PAD.r) * m) / 12;
  const y = (v: number) => PAD.t + ((height - PAD.t - PAD.b) * (hi - v)) / (hi - lo);
  const path = (points: readonly AgainstBudgetPoint[]) =>
    points.map((p, i) => `${i ? 'L' : 'M'}${x(p.m).toFixed(1)} ${y(p.vsBudgetSt).toFixed(1)}`).join('');
  const optimistic = view.scenarios.find((s) => s.name === 'Optimistic')!;
  const conservative = view.scenarios.find((s) => s.name === 'Conservative')!;
  const fan = `${path(optimistic.points)}L${[...conservative.points].reverse().map((p) => `${x(p.m).toFixed(1)} ${y(p.vsBudgetSt).toFixed(1)}`).join(' L')}Z`;
  const toDate = view.actual[view.actual.length - 1]!;
  const behind = `${path(view.actual)}L${x(toDate.m).toFixed(1)} ${y(0).toFixed(1)} L${x(0).toFixed(1)} ${y(0).toFixed(1)}Z`;
  const end = (name: PlanningScenarioName) => lastOf(view.scenarios.find((s) => s.name === name)!.points).vsBudgetSt;
  const labels: { value: number; label: string; name?: PlanningScenarioName; nudge: number }[] = [
    { value: end('Optimistic'), label: 'Optimistic', name: 'Optimistic', nudge: 0 },
    { value: end('Central'), label: 'Central', name: 'Central', nudge: -3 },
    { value: end('Conservative'), label: 'Conservative', name: 'Conservative', nudge: 9 },
    { value: lastOf(view.stretch).vsBudgetSt, label: view.stretchLabel, nudge: 0 },
    { value: 0, label: 'Budget', nudge: 0 },
  ];
  const drawOrder: PlanningScenarioName[] = ['Optimistic', 'Conservative', 'Central'].filter((n) => n !== selected) as PlanningScenarioName[];
  drawOrder.push(selected);

  return (
    <div ref={ref} className="min-w-0">
      <svg
        viewBox={`0 0 ${W} ${height}`}
        width="100%"
        role="img"
        aria-label="Year 1 cumulative feed against cumulative budget: behind budget to date, three forecast scenarios to Feb 2027 against the budget and stretch lines"
        data-year-against-budget
        className="block overflow-visible"
      >
        {view.ticks.map((tick) => (
          <g key={tick}>
            {tick !== 0 ? <line x1={PAD.l} x2={W - PAD.r} y1={y(tick)} y2={y(tick)} stroke="var(--hairline)" /> : null}
            <text x={PAD.l - 7} y={y(tick) + 3.5} textAnchor="end" fontSize="10" fill="var(--ink-tertiary)" className="tabular-nums">
              {formatVsBudgetTick(tick)}
            </text>
          </g>
        ))}
        <path d={behind} fill="color-mix(in srgb, var(--state-warning) 13%, transparent)" data-behind-budget />
        <path d={fan} fill="color-mix(in srgb, var(--ink-secondary) 16%, transparent)" />
        <line x1={x(0)} x2={x(12)} y1={y(0)} y2={y(0)} stroke="var(--ink-secondary)" strokeWidth="1.4" strokeDasharray="5 4" />
        <path d={path(view.stretch)} fill="none" stroke="var(--ink-tertiary)" strokeWidth="1.2" strokeDasharray="2 3" />
        {drawOrder.map((name) => {
          const scenario = view.scenarios.find((s) => s.name === name)!;
          const on = name === selected;
          return (
            <path
              key={name}
              d={path(scenario.points)}
              data-scenario-path={name}
              fill="none"
              stroke={on ? 'var(--ink-primary)' : 'var(--ink-tertiary)'}
              strokeWidth={on ? 2 : 1}
              strokeDasharray={on ? '6 3' : '2 3'}
              className="transition-[stroke] duration-150 motion-reduce:transition-none"
            />
          );
        })}
        <path d={path(view.actual)} fill="none" stroke="var(--ink-primary)" strokeWidth="2.2" />
        {view.actual.slice(1).map((p, i, all) => (
          <circle key={p.m} cx={x(p.m)} cy={y(p.vsBudgetSt)} r={i === all.length - 1 ? 4 : 2.6} fill="var(--ink-primary)" stroke="var(--surface-2)" strokeWidth="1.5" />
        ))}
        <line x1={x(view.cursorM)} x2={x(view.cursorM)} y1={PAD.t} y2={height - PAD.b} stroke="var(--ink-tertiary)" strokeDasharray="2 3" />
        <text x={x(view.cursorM) + 6} y={PAD.t + 10} fontSize="10" fill="var(--ink-secondary)">to date</text>
        {labels.map((label) => {
          const on = label.name === selected;
          return (
            <text
              key={label.label}
              x={x(12) + 8}
              y={y(label.value) + 3.5 + label.nudge}
              fontSize="10.5"
              fontWeight={on ? 600 : 500}
              fill={on ? 'var(--ink-primary)' : 'var(--ink-tertiary)'}
            >
              {label.label}
            </text>
          );
        })}
        {view.months.map((month, index) => (
          <g key={month.code}>
            <text x={x(index + 0.5)} y={height - 16} textAnchor="middle" fontSize="10.5" fontWeight="600" fill={index === view.currentMonthIndex ? 'var(--ink-primary)' : 'var(--ink-secondary)'}>
              {month.code}
            </text>
            <text x={x(index + 0.5)} y={height - 4} textAnchor="middle" fontSize="10" fill="var(--ink-tertiary)">
              {month.month}
            </text>
          </g>
        ))}
      </svg>
      <p className="mt-1.5 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs text-muted">
        <LegendMark kind="actual" />Actual
        <LegendMark kind="forecast" />Forecast · selected scenario
        <LegendMark kind="fan" />Conservative – optimistic
        <LegendMark kind="budget" />Budget = 0
        <LegendMark kind="behind" />Behind budget
      </p>
    </div>
  );
}

function LegendMark({ kind }: { kind: 'actual' | 'forecast' | 'fan' | 'budget' | 'behind' }) {
  if (kind === 'fan' || kind === 'behind') {
    return (
      <span
        aria-hidden="true"
        className="-mr-2 inline-block h-2 w-4 rounded-[2px]"
        style={{ background: kind === 'fan' ? 'color-mix(in srgb, var(--ink-secondary) 16%, transparent)' : 'color-mix(in srgb, var(--state-warning) 13%, transparent)' }}
      />
    );
  }
  const border = kind === 'actual' ? '2px solid var(--ink-primary)' : kind === 'forecast' ? '1.5px dashed var(--ink-secondary)' : '1.5px dashed var(--ink-tertiary)';
  return <span aria-hidden="true" className="-mr-2 inline-block w-4" style={{ borderTop: border }} />;
}
